// src/components/BottomNavBar.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './icon';

// --- Visual Constants ---
const BAR_HEIGHT = 58;
const NOTCH_RADIUS = 28;
const CHIP_DIAMETER = 48;
const BAR_CORNER_RADIUS = 20;

const NAV_ITEMS = [
  { label: 'dashboard', to: '/', icon: 'home', match: (p) => p === '/' },
  { label: 'trade', to: '/trade', icon: 'chart', match: (p) => p === '/trade' },
  { label: 'forex', to: '/forex', icon: 'chart-bar', match: (p) => p === '/forex' },
  { label: 'history', to: '/trade-history', icon: 'history', match: (p) => p.startsWith('/trade-history') },
  { label: 'wallet', to: '/wallet', icon: 'wallet', match: (p) => p.startsWith('/wallet') },
  { label: 'profile', to: '/profile', icon: 'user', match: (p) => p.startsWith('/profile') },
];

export default function BottomNavBar() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const activeIndex = NAV_ITEMS.findIndex((item) => item.match(pathname));
  const hasActiveItem = activeIndex >= 0;
  const visualIndex = hasActiveItem ? activeIndex : 0;

  const navRef = useRef(null);
  const [width, setWidth] = useState(360);
  
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const resizeObserver = new ResizeObserver(() => setWidth(el.clientWidth || 360));
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  const notchCenterX = useMemo(() => {
    const segmentWidth = width / NAV_ITEMS.length;
    return segmentWidth * visualIndex + segmentWidth / 2;
  }, [width, visualIndex]);

  const activeItem = hasActiveItem ? NAV_ITEMS[activeIndex] : null;

  // --- HIDE ON AGENT PORTAL (Must be placed after all React Hooks) ---
  if (pathname.startsWith('/agent')) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      className="md:hidden fixed left-1/2 z-40 -translate-x-1/2 w-[94%] max-w-md"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}
    >
      <div className="relative select-none">
        <svg
          width="100%"
          height={BAR_HEIGHT + NOTCH_RADIUS}
          viewBox={`0 0 ${width} ${BAR_HEIGHT + NOTCH_RADIUS}`}
          className="drop-shadow-[0_-8px_25px_rgba(0,0,0,0.6)]"
        >
          <defs>
            <mask id="notch-mask">
              <rect x="0" y={NOTCH_RADIUS} width={width} height={BAR_HEIGHT} rx={BAR_CORNER_RADIUS} ry={BAR_CORNER_RADIUS} fill="white" />
              <g style={{ transition: "transform 280ms ease-in-out" }} transform={`translate(${notchCenterX}, 0)`}>
                <circle
                  cx="0"
                  cy={NOTCH_RADIUS + 6}
                  r={hasActiveItem ? NOTCH_RADIUS : 0}
                  fill="black"
                />
              </g>
            </mask>
            <linearGradient id="premium-nav-bg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#161c2d" />
              <stop offset="100%" stopColor="#0b1020" />
            </linearGradient>
          </defs>

          <rect
            x="0"
            y={NOTCH_RADIUS}
            width={width}
            height={BAR_HEIGHT}
            rx={BAR_CORNER_RADIUS}
            ry={BAR_CORNER_RADIUS}
            fill="url(#premium-nav-bg)"
            mask="url(#notch-mask)"
          />
        </svg>

        {activeItem && (
          <Link
            to={activeItem.to}
            aria-label={t(activeItem.label)}
            aria-current="page"
            className="absolute will-change-transform"
            style={{
              left: notchCenterX - CHIP_DIAMETER / 2,
              top: NOTCH_RADIUS + 6 - CHIP_DIAMETER / 2,
              width: CHIP_DIAMETER,
              height: CHIP_DIAMETER,
              transition: "left 280ms ease-in-out",
            }}
          >
            <div className="w-full h-full rounded-full flex items-center justify-center bg-gradient-to-tr from-blue-600 to-sky-400 shadow-[0_0_24px_rgba(56,189,248,0.48)] border border-white/20">
              <ActiveIconComponent index={activeIndex} />
            </div>
          </Link>
        )}

        <div className="absolute inset-x-0" style={{ top: NOTCH_RADIUS, height: BAR_HEIGHT }}>
          <div className="flex h-full">
            {NAV_ITEMS.map(({ to, label }, i) => {
              const isActive = i === activeIndex;
              return (
                <Link
                  key={to}
                  to={to}
                  aria-label={t(label)}
                  aria-current={isActive ? "page" : undefined}
                  className="relative flex-1 flex items-center justify-center h-full active:scale-95 transition-transform"
                >
                  <Icon
                    name={NAV_ITEMS[i].icon}
                    className={`h-6 w-6 transition-all duration-300 ${
                      isActive 
                        ? "opacity-0 scale-50" 
                        : "opacity-100 fill-[#64748b] hover:fill-[#e2e8f0] hover:scale-110 drop-shadow-md"
                    }`}
                  />
                  <span className="sr-only">{t(label)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
}

function ActiveIconComponent({ index }) {
  const activeIconName = NAV_ITEMS[index]?.icon || 'home';
  return <Icon name={activeIconName} className="h-6 w-6 text-white fill-white" />;
}